/**
 * Thin wrappers over the brand kit's component classes (Visualguideline/tokens/nordiska.css).
 * They exist so the rules live in one place rather than being remembered at every call site.
 * No colour, radius or spacing value is written here — it all comes from the kit.
 */
import type { ButtonHTMLAttributes, HTMLAttributes, InputHTMLAttributes, ReactNode } from "react";
import { useId } from "react";

/**
 * `cta` is Glöden and carries Djupet text. The brand guide allows **one CTA per view** — if a
 * second action looks important, it is a `primary`, not a CTA.
 */
export type ButtonVariant = "default" | "primary" | "cta" | "ghost" | "danger";

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
};

export function Button({ variant = "default", className = "", ...rest }: ButtonProps) {
  const variantClass = variant === "default" ? "" : ` nb-btn--${variant}`;
  return <button className={`nb-btn${variantClass} ${className}`.trim()} {...rest} />;
}

type FieldProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  help?: string;
  error?: string;
  /** Numeric values wear IBM Plex Mono with tabular figures — a brand signature. */
  numeric?: boolean;
};

export function Field({ label, help, error, numeric, id, className = "", ...rest }: FieldProps) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const describedBy = error ? `${fieldId}-error` : help ? `${fieldId}-help` : undefined;

  return (
    <div className="grid gap-s2">
      {/* Label above the field, never a placeholder standing in for one. */}
      <label className="nb-overline border-0 pb-0" htmlFor={fieldId}>
        {label}
      </label>
      <input
        id={fieldId}
        className={`nb-field${numeric ? " nb-field--num" : ""}${
          error ? " nb-field--invalid" : ""
        } ${className}`.trim()}
        inputMode={numeric ? "decimal" : undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        {...rest}
      />
      {help && !error && (
        <span id={`${fieldId}-help`} className="text-small text-muted">
          {help}
        </span>
      )}
      {error && (
        <span id={`${fieldId}-error`} className="text-small text-fail-text">
          {error}
        </span>
      )}
    </div>
  );
}

type CardProps = HTMLAttributes<HTMLDivElement> & {
  hero?: boolean;
  sunken?: boolean;
};

export function Card({ hero, sunken, className = "", ...rest }: CardProps) {
  const modifiers = `${hero ? " nb-card--hero" : ""}${sunken ? " nb-card--sunken" : ""}`;
  return <div className={`nb-card${modifiers} ${className}`.trim()} {...rest} />;
}

export function Badge({ status, children }: { status: "pass" | "fail" | "warn"; children: ReactNode }) {
  return <span className={`nb-badge nb-badge--${status}`}>{children}</span>;
}

/** Every numeric value in the product goes through here. */
export function Num({ className = "", ...rest }: HTMLAttributes<HTMLSpanElement>) {
  return <span className={`nb-num ${className}`.trim()} {...rest} />;
}

/** A keycap, for documenting shortcuts in the interface itself. */
export function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="nb-num rounded-control border border-line-strong bg-surface-2 px-s2 py-s1 text-small text-muted">
      {children}
    </kbd>
  );
}

/** The cyan left bar the brand guide calls its signature device. */
export function AccentBlock({ className = "", ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={`nb-accent-block ${className}`.trim()} {...rest} />;
}
